import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  name?: string;
  avatarUrl?: string;
  role: 'SUPERADMIN' | 'OWNER' | 'ADMIN' | 'PARTNER';
  emailVerified: boolean;
  createdAt: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string; // Optional - may not be returned if stored in HttpOnly cookie
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresTwoFactorSetup: boolean; // True if admin user needs to set up 2FA

  // Actions
  setAuth: (user: User, tokens: AuthTokens, requiresTwoFactorSetup?: boolean) => void;
  setTokens: (tokens: AuthTokens, requiresTwoFactorSetup?: boolean) => void;
  setUser: (user: User) => void;
  setLoading: (loading: boolean) => void;
  setRequiresTwoFactorSetup: (required: boolean) => void;
  logout: () => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      requiresTwoFactorSetup: false,

      setAuth: (user, tokens, requiresTwoFactorSetup = false) => {
        // Store only access token in localStorage for API client
        // Refresh token is now stored in HttpOnly cookie (secure)
        localStorage.setItem('accessToken', tokens.accessToken);
        // Clean up old refresh token from localStorage (migrating to HttpOnly cookies)
        localStorage.removeItem('refreshToken');

        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: null, // Refresh token is now in HttpOnly cookie
          isAuthenticated: true,
          isLoading: false,
          requiresTwoFactorSetup,
        });
      },

      setTokens: (tokens, requiresTwoFactorSetup) => {
        localStorage.setItem('accessToken', tokens.accessToken);
        // Clean up old refresh token from localStorage (migrating to HttpOnly cookies)
        localStorage.removeItem('refreshToken');

        set((state) => ({
          accessToken: tokens.accessToken,
          refreshToken: null, // Refresh token is now in HttpOnly cookie
          requiresTwoFactorSetup: requiresTwoFactorSetup ?? state.requiresTwoFactorSetup,
        }));
      },

      setUser: (user) => set({ user }),

      setLoading: (isLoading) => set({ isLoading }),

      setRequiresTwoFactorSetup: (required) => set({ requiresTwoFactorSetup: required }),

      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          requiresTwoFactorSetup: false,
        });
      },

      clearAuth: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          requiresTwoFactorSetup: false,
        });
      },
    }),
    {
      name: 'tremoria-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        // refreshToken is now in HttpOnly cookie, no longer persisted
        isAuthenticated: state.isAuthenticated,
        requiresTwoFactorSetup: state.requiresTwoFactorSetup,
      }),
      onRehydrateStorage: () => (state) => {
        // Set isLoading to false once hydration is complete
        if (state) {
          state.isLoading = false;

          // Sync access token to localStorage for API client
          // This ensures API client can read tokens after page refresh
          if (state.accessToken && state.isAuthenticated) {
            localStorage.setItem('accessToken', state.accessToken);
          } else {
            // Clear stale tokens if user is not authenticated
            localStorage.removeItem('accessToken');
          }
          // Always clean up old refresh token from localStorage (migrating to HttpOnly cookies)
          localStorage.removeItem('refreshToken');
        }
      },
    }
  )
);
